import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import '../styles/AccountManager.css';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AccountManager = ({ isOpen, onClose, isFullPage }) => {
  const { user, token, logout } = useAuth();
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [securityQuestion1, setSecurityQuestion1] = useState('');
  const [securityQuestion2, setSecurityQuestion2] = useState('');
  const [answer1, setAnswer1] = useState('');
  const [answer2, setAnswer2] = useState('');

  // Security questions from translations
  const SECURITY_QUESTIONS = useMemo(() => [
    t('account.securityQuestions.q1'),
    t('account.securityQuestions.q2'),
    t('account.securityQuestions.q3'),
    t('account.securityQuestions.q4'),
    t('account.securityQuestions.q5'),
    t('account.securityQuestions.q6'),
    t('account.securityQuestions.q7'),
    t('account.securityQuestions.q8')
  ], [t]);
  
  const filteredQuestions2 = useMemo(
    () => SECURITY_QUESTIONS.filter(q => q !== securityQuestion1),
    [SECURITY_QUESTIONS, securityQuestion1]
  );

  if (!isOpen) return null;

  const handleDeleteAccount = async () => {
    if (deleteInput !== 'DELETE') {
      alert(t('account.typeDelete'));
      return;
    }

    if (!window.confirm(t('account.deleteConfirmMsg'))) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/delete-account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert(t('account.deleteSuccess'));
        logout();
        onClose();
      } else {
        const error = await response.json();
        alert(`${t('account.deleteFailed')}: ${error.message}`);
      }
    } catch (error) {
      logger.error('Error deleting account:', error);
      alert(t('account.deleteFailed'));
    }
  };

  const handleForgotPassword = async () => {
    if (!securityQuestion1 || !securityQuestion2 || !answer1 || !answer2) {
      alert(t('account.answerBothQuestions'));
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: user?.email,
          security_question_1: securityQuestion1,
          security_answer_1: answer1,
          security_question_2: securityQuestion2,
          security_answer_2: answer2
        })
      });

      if (response.ok) {
        alert(`✅ ${t('account.passwordResetSuccess')}`);
        setShowForgotPassword(false);
        setAnswer1('');
        setAnswer2('');
      } else {
        const error = await response.json();
        alert(`❌ ${error.message || t('account.securityAnswersIncorrect')}`);
      }
    } catch (error) {
      logger.error('Error resetting password:', error);
      alert(t('account.passwordResetFailed'));
    }
  };

  const renderAccountContent = () => (
    <>
      {/* Account Info */}
      <section className="account-section">
        <h3>📋 {t('account.accountInfo')}</h3>
        
        <div className="info-card">
          <div className="info-row">
            <span className="info-label">{t('account.usernameEmail')}:</span>
            <span className="info-value">{user?.email || user?.name || t('account.notAvailable')}</span>
          </div>

          <div className="info-row">
            <span className="info-label">{t('account.displayName')}:</span>
            <span className="info-value">{user?.name || t('account.notAvailable')}</span>
          </div>

          <div className="info-row">
            <span className="info-label">{t('account.accountType')}:</span>
            <span className="info-value">
              {user?.is_guest ? `🆓 ${t('account.guest')}` : `✅ ${t('account.registered')}`}
            </span>
          </div>

          <div className="info-row">
            <span className="info-label">{t('account.userId')}:</span>
            <span className="info-value" style={{ fontSize: '12px', color: '#666' }}>
              {user?.user_id || t('account.notAvailable')}
            </span>
          </div>
        </div>
      </section>

      {/* Notification Settings */}
      <section className="account-section">
        <h3>🔔 {t('account.notificationSettings')}</h3>
        <div className="info-card">
          <div className="setting-row">
            <div className="setting-info">
              <span className="setting-label">{t('account.notificationSound')}</span>
              <span className="setting-description">{t('account.notificationSoundDesc')}</span>
            </div>
            <label className="toggle-switch">
              <input 
                type="checkbox" 
                checked={localStorage.getItem('notificationSoundEnabled') !== 'false'}
                onChange={(e) => {
                  localStorage.setItem('notificationSoundEnabled', JSON.stringify(e.target.checked));
                  window.dispatchEvent(new Event('notificationSoundChanged'));
                }}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </section>

      {/* Password Section */}
      <section className="account-section">
        <h3>🔐 {t('account.security')}</h3>
        <div className="password-section">
          <p className="password-note">
            ⚠️ <strong>{t('account.passwordManagement')}:</strong> {t('account.passwordNote')}
          </p>
          
          {!showForgotPassword ? (
            <button 
              className="btn-forgot-password"
              onClick={() => setShowForgotPassword(true)}
              data-testid="forgot-password-btn"
            >
              🔑 {t('account.forgotPassword')}
            </button>
          ) : (
            <div className="forgot-password-form">
              <h4>🔐 {t('account.passwordRecovery')}</h4>
              <p className="help-text">{t('account.passwordRecoveryHelp')}</p>
              
              <div className="form-group">
                <label>{t('account.securityQuestion1')}:</label>
                <select 
                  value={securityQuestion1}
                  onChange={(e) => setSecurityQuestion1(e.target.value)}
                  className="security-select"
                  data-testid="security-question-1"
                >
                  <option value="">{t('account.selectQuestion')}</option>
                  {SECURITY_QUESTIONS.map((q) => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={answer1}
                  onChange={(e) => setAnswer1(e.target.value)}
                  placeholder={t('account.yourAnswer')}
                  className="security-input"
                  data-testid="security-answer-1"
                />
              </div>

              <div className="form-group">
                <label>{t('account.securityQuestion2')}:</label>
                <select 
                  value={securityQuestion2}
                  onChange={(e) => setSecurityQuestion2(e.target.value)}
                  className="security-select"
                  data-testid="security-question-2"
                >
                  <option value="">{t('account.selectQuestion')}</option>
                  {filteredQuestions2.map((q) => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={answer2}
                  onChange={(e) => setAnswer2(e.target.value)}
                  placeholder={t('account.yourAnswer')}
                  className="security-input"
                  data-testid="security-answer-2"
                />
              </div>

              <div className="form-actions">
                <button 
                  className="btn-cancel"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setAnswer1('');
                    setAnswer2('');
                  }}
                >
                  {t('misc.cancel')}
                </button>
                <button 
                  className="btn-submit"
                  onClick={handleForgotPassword}
                  disabled={!securityQuestion1 || !securityQuestion2 || !answer1 || !answer2}
                  data-testid="send-password-btn"
                >
                  📧 {t('account.sendPasswordEmail')}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Danger Zone */}
      {!user?.is_guest && (
        <section className="account-section danger-zone">
          <h3>⚠️ {t('account.dangerZone')}</h3>
          
          {!showDeleteConfirm ? (
            <div className="danger-content">
              <p>{t('account.deleteAccountDesc')}</p>
              <button 
                className="btn-danger"
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="delete-account-btn"
              >
                🗑️ {t('account.deleteAccount')}
              </button>
            </div>
          ) : (
            <div className="delete-confirm">
              <p className="warning-text">
                ⚠️ <strong>{t('account.deleteWarning')}</strong>
              </p>
              <ul className="warning-list">
                <li>{t('account.deleteWarningItems.account')}</li>
                <li>{t('account.deleteWarningItems.gameHistory')}</li>
                <li>{t('account.deleteWarningItems.friends')}</li>
                <li>{t('account.deleteWarningItems.purchases')}</li>
                <li>{t('account.deleteWarningItems.settings')}</li>
              </ul>
              
              <div className="confirm-input">
                <label>{t('account.typeDelete')}</label>
                <input
                  type="text"
                  value={deleteInput}
                  onChange={(e) => setDeleteInput(e.target.value)}
                  placeholder={t('account.typeDeletePlaceholder')}
                  className="delete-input"
                  data-testid="delete-confirm-input"
                />
              </div>

              <div className="confirm-actions">
                <button 
                  className="btn-cancel"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteInput('');
                  }}
                >
                  {t('misc.cancel')}
                </button>
                <button 
                  className="btn-delete"
                  onClick={handleDeleteAccount}
                  disabled={deleteInput !== 'DELETE'}
                  data-testid="confirm-delete-btn"
                >
                  {t('account.deleteForever')}
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </>
  );

  // If full page, render without modal wrapper
  if (isFullPage) {
    return (
      <div className="account-manager-content" style={{ padding: 0, maxHeight: 'none' }}>
        {renderAccountContent()}
      </div>
    );
  }

  // Render as modal
  return (
    <div className="account-manager-overlay" onClick={onClose}>
      <div className="account-manager-modal" onClick={(e) => e.stopPropagation()}>
        <div className="account-manager-header">
          <h2>👤 {t('account.title')}</h2>
          <button className="close-button" onClick={onClose} data-testid="account-close">×</button>
        </div>

        <div className="account-manager-content">
          {renderAccountContent()}
        </div>
      </div>
    </div>
  );
};

export default AccountManager;
